<template>
  <b-card>
    <!-- Destino de QR -->
    <span class="text-primary font-weight-bold">{{ $t('message.qrDestination') }}</span>
    <b-row>
      <b-col md="4" class="mb-3 mt-1">
        <v-select
          id="qrOptions"
          v-model="qrData.destination"
          :reduce="item => item.value"
          label="title"
          :options="qrOptions"
          :placeholder="$t('placeholder.codeDestination')"
          :disabled="isLocked"
        />
      </b-col>
    </b-row>

    <!-- Customer and Employee Survey Options-->
    <template v-if="qrData.destination">
      <span class="text-primary font-weight-bold">{{ qrData.destination === 'barometer-survey' ? $t('message.selectSurveyToGenerateQR'): $t('message.urlInformation') }}
      </span>
      <b-row class="mt-1 mb-1">
        <b-col md="4" class="pb-1" v-if="(qrData.destination === 'employee-survey' && physical_location_in_daily_dialogue)">
          <v-select
            v-model="qrData.physical_location"
            :reduce="item => item.value"
            label="title"
            :options="physical_locations"
            :placeholder="$t('placeholder.physical_location')"
            :disabled="isLocked"
          />
        </b-col>
        <b-col md="4" class="pb-1" v-if="qrData.destination === 'employee-survey'">
          <v-select
            v-model="qrData.location"
            :reduce="item => item.value"
            label="title"
            :options="filteredLocations"
            :placeholder="$t('placeholder.locations')"
            :disabled="isLocked"
          />
        </b-col>

        <b-col md="4" v-if="qrData.destination === 'customer-survey'">
          <v-select
            v-model="qrData.feedbackPoint"
            label="title"
            :options="feedbackPoints"
            :placeholder="$t('placeholder.feedbackPoint')"
            :disabled="isLocked"
          />
        </b-col>
      </b-row>
    </template>

    <!-- Barometer Surveys List -->
    <b-row v-if="qrData.destination === 'barometer-survey'">
      <b-col sm="4">
        <b-form-group>
          <b-form-input
            v-model="searchQuery"
            type="search"
            class="d-inline-block"
            :placeholder="$t('message.search')"
            :disabled="isLocked"
            @input="searchBarometerSurveyCreated"
          />
        </b-form-group>
      </b-col>

      <b-col cols="12">
        <b-table
          ref="refBarometerSurveyCreatedListTable"
          :items="paginatedBarometerSurveysCreated"
          hover
          responsive
          :fields="tableColumns"
          primary-key="id"
          :sort-by.sync="sortBy"
          show-empty=""
          :empty-text="isLoading ? $t('message.loading') : $t('message.no_records')"
          :sort-desc.sync="isSortDirDesc"
        >
          <template #cell(physical_location)="data">
            {{ data.item.physical_location ? data.item.physical_location : '-' }}
          </template>
          <template #cell(actions)="data">
            <b-form-checkbox :disabled="isLocked" :checked="selectedSurvey.id === data.item._id" @change="() => selectBarometerSurvey(data.item)" />
          </template>
        </b-table>
      </b-col>

      <b-col
        cols="12"
        class="d-flex justify-content-between flex-wrap"
      >
        <div class="mb-1">
          <b-form-group class="mb-0">
            <label class="d-inline-block text-sm-left mr-50">{{ $t('message.per_page') }}</label>
            <b-form-select
              id="perPageSelect"
              v-model="perPage"
              size="sm"
              :options="perPageOptions"
              class="w-50"
            />
          </b-form-group>
        </div>

        <div class="mb-1">
          <span class="text-muted">{{ showingMessage }}</span>
        </div>

        <b-pagination
          v-model="currentPage"
          :total-rows="totalBarometerSurveysCreated"
          :per-page="perPage"
          first-number=""
          last-number=""
          prev-class="prev-item"
          next-class="next-item"
        />
      </b-col>
    </b-row>

    <!-- Check URL and Create QR -->
    <b-row>
      <b-col v-if="computedUrl" class="mb-1 d-flex flex-column justify-start align-center">
        <span v-if="!urlAlreadyExists && qrData.destination !== 'barometer-survey'" class="font-weight-bold text-primary">{{ $t('message.checkGeneratedUrl') }}:</span><br>
        <div :class="qrData.destination !== 'barometer-survey' ? 'mb-1' :  ''">
          <code class="text-secondary font-weight-bold">{{ computedUrl }}</code>
        </div>
      </b-col>
      <b-col md="12">
        <div class="mt-0 mb-2">
          <span v-if="urlAlreadyExists" class="font-weight-bold text-danger">{{ $t('message.existingQrWarning')}}</span>
        </div>
        <b-button
          size="md"
          variant="primary"
          @click="generateQR"
          :disabled="!isFormValid || isLocked"
        >
          <span class="mr-25 align-middle">{{ $t('message.generateQr') }}</span>
        </b-button>

        <div id="qr-container" ref="qrContainer" class="my-2"></div>
        <div v-if="isLoading && isFormValid">{{ $t('message.loading') }}</div>
        <div v-else>
          <b-button
            v-if="qrCreated"
            size="md"
            variant="outline-primary"
            @click="downloadQR" 
          >
            <span class="mr-25 align-middle">{{ $t('message.downloadQr') }}</span>
          </b-button>

          <b-button
            v-if="isLocked"
            size="md"
            variant="success"
            class="ml-2"
            @click="resetForm"
          >
            {{ $t('message.createNewQr') }}
          </b-button>
        </div>
      </b-col>
    </b-row>

  </b-card>
</template>

<script>
import { onMounted, computed, watch } from '@vue/composition-api'
import vSelect from 'vue-select'
import { BButton } from 'bootstrap-vue'
import useqrCodesAdd from './useQRCodeAdd'
import { BPagination, BTable, BFormCheckbox } from 'bootstrap-vue'

export default {
  components: {
    vSelect,
    BButton,
    BTable,
    BPagination,
    BFormCheckbox
  },

  setup() {
    const {
      qrContainer,
      qrCreated,
      generateQR,
      downloadQR,
      qrOptions,
      qrData,
      physical_locations,
      locations,
      feedbackPoints,
      isFormValid,
      computedUrl,
      resetForm,
      isLocked,
      isLoading,
      router,
      getLocationsForDropDown,
      getPhysicalLocationsForDropDown,
      getFeedbackPointsForDropDown,
      physical_location_in_daily_dialogue,
      fetchQRCodes,
      urlAlreadyExists,

      // Barometer Surveys 
      fetchBarometerSurveysCreated,
      tableColumns,
      perPage,
      currentPage,
      perPageOptions,
      totalBarometerSurveysCreated,
      showingMessage,
      searchQuery,
      sortBy,
      isSortDirDesc,
      barometerSurveysCreated,
      searchBarometerSurveyCreated,
      selectedSurvey,
      selectBarometerSurvey,
    } = useqrCodesAdd()


    // Barometer Surveys 
    const paginatedBarometerSurveysCreated = computed(() => {
      const start = (currentPage.value - 1) * perPage.value
      const end = start + perPage.value
      return barometerSurveysCreated.value.slice(start, end)
    })

    // Filtrar departamentos en base a la localidad seleccionada
    const filteredLocations = computed(() => {
      // Si no seleccionaron physical_locations → devolver todas
      if (!qrData.value.physical_location) return locations.value

      return locations.value.filter(loc =>
        qrData.value.physical_location === loc.physical_location
      )
    })

    watch(() => qrData.value.physical_location, () => {
      qrData.value.location = qrData.value.location && 
        filteredLocations.value.some(l => l.value === qrData.value.location)
        ? qrData.value.location
        : null
    })

    onMounted(async () => {
      getPhysicalLocationsForDropDown()
      getLocationsForDropDown()
      getFeedbackPointsForDropDown()
      fetchQRCodes()
      
      // Barometer Surveys 
      fetchBarometerSurveysCreated()
    })

    return {
      qrContainer,
      qrCreated,
      generateQR,
      downloadQR,
      qrOptions,
      qrData,
      physical_locations,
      filteredLocations,
      feedbackPoints,
      isFormValid,
      computedUrl,
      resetForm,
      isLocked,
      isLoading,
      router,
      getPhysicalLocationsForDropDown,
      physical_location_in_daily_dialogue,
      fetchQRCodes,
      urlAlreadyExists,

      // Barometer Surveys 
      barometerSurveysCreated,
      showingMessage,
      tableColumns,
      searchQuery,
      sortBy,
      isSortDirDesc,
      totalBarometerSurveysCreated,
      currentPage,
      perPageOptions,
      perPage,
      isLoading,
      searchBarometerSurveyCreated,
      paginatedBarometerSurveysCreated,
      getLocationsForDropDown,
      selectedSurvey,
      selectBarometerSurvey,
    }
  },
}
</script>

